import { ChangeEvent, ComponentProps, useState } from 'react';
import { DateRange, DayPicker } from 'react-day-picker';
import { useTranslation } from 'react-i18next';

import { DATE_PRESETS } from './constants';
import { DatePickerItem } from './DatePickerItem';
import { DatePresetRange } from './types';
import { getDateByPreset } from './utils';
import { format } from 'date-fns';

import { ChevronLeftIcon, ChevronRightIcon } from '@/assets/icons';
import { cn } from '@/utils/tailwind';

import { Button } from '../../atoms';
import { Input } from '../../atoms/Input/Input';

type CalendarProps = ComponentProps<typeof DayPicker> & {
  mode: 'range';
  defaultDate: DateRange | undefined;
  selectedPreset?: DatePresetRange | null;
  onSelect?: (value: DateRange | undefined) => void;
  onPresetChange?: (preset: DatePresetRange | null) => void;
};

const parseDefaultDate = (date: DateRange | undefined) => {
  return {
    from: date?.from ? new Date(date.from) : undefined,
    to: date?.to ? new Date(date.to) : undefined,
  };
};

const Calendar = ({
  className,
  classNames,
  defaultDate,
  showOutsideDays = true,
  mode = 'range',
  selectedPreset,
  onSelect,
  onPresetChange,
  ...props
}: CalendarProps) => {
  const { t } = useTranslation('components');

  const [date, setDate] = useState<DateRange | undefined>(parseDefaultDate(defaultDate));
  const [activePreset, setActivePreset] = useState<DatePresetRange | null>(selectedPreset ?? null);

  const handlePresetClick = (range: DatePresetRange) => {
    const { start, end } = getDateByPreset(range);

    setDate({ from: start, to: end });

    setActivePreset(range);
  };
  const handleStartDateChange = (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const newStart = new Date(e.target.value);
    setDate({ ...date, from: newStart });
  };

  const handleEndDateChange = (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const newEnd = new Date(e.target.value);
    setDate({ from: date?.from, to: newEnd });
  };

  const handleSelect = (newDate: DateRange | undefined) => {
    setDate(newDate);
    setActivePreset(null);
  };

  const handleCancelClick = () => {
    setDate(defaultDate);
    onSelect?.(defaultDate);
    onPresetChange?.(activePreset);
  };

  const handleSaveClick = () => {
    onSelect?.(date);
    onPresetChange?.(activePreset);
  };

  return (
    <div className="relative flex shadow-custom-calendar border border-custom-warm-grey-200 w-fit bg-custom-warm-grey-25">
      <div className="flex flex-col  gap-1 px-4 py-3 border-r border-r-custom-warm-grey-200">
        {DATE_PRESETS.map(({ label, value }) => (
          <DatePickerItem
            key={value}
            onClick={() => handlePresetClick(value)}
            className="w-full text-left justify-start"
            isActive={activePreset === value}
          >
            {label}
          </DatePickerItem>
        ))}
      </div>

      <div className="flex flex-col">
        <DayPicker
          showOutsideDays={showOutsideDays}
          navLayout="around"
          className={cn(
            'bg-custom-warm-grey-25 w-fit border-b border-b-custom-warm-grey-200',
            className
          )}
          classNames={{
            months: 'flex flex-col sm:flex-row gap-2',
            // `relative` lets the `navLayout="around"` prev/next buttons anchor to each month's corners
            month: 'relative flex flex-col gap-3 px-6 py-5',
            // v9 `caption` → v10 `month_caption` (label-only wrapper; nav buttons are now siblings)
            month_caption: 'flex justify-center pt-1 items-center w-full',
            caption_label: 'text-body-16-medium text-custom-warm-grey-700',
            // v9 `nav_button*` → v10 `button_previous` / `button_next`; positioned against `month` to mimic the legacy in-caption placement
            button_previous: cn(
              'absolute top-5 left-6 size-7 bg-transparent p-0 opacity-50 hover:opacity-100 px-1.5 -mt-0.5'
            ),
            button_next: cn(
              'absolute top-5 right-6 size-7 bg-transparent p-0 opacity-50 hover:opacity-100 px-1.5 -mt-0.5'
            ),
            // v9 `table`/`head_row`/`head_cell`/`row` → v10 `month_grid`/`weekdays`/`weekday`/`week`
            month_grid: 'w-full border-collapse space-x-1',
            weekdays: 'flex',
            weekday:
              'size-10 flex items-center justify-center text-body-14-medium text-custom-warm-grey-700',
            week: 'flex w-full mt-2',
            // v9 `cell` → v10 `day` (the <td>); v9 `day` → v10 `day_button` (the inner <button>)
            day: cn(
              'relative size-10 items-center justify-center text-body-14-regular text-custom-warm-grey-700 focus-within:relative focus-within:z-20',
              'first:[&:has([aria-selected])]:rounded-tl-[50%] first:[&:has([aria-selected])]:rounded-bl-[50%] last:[&:has([aria-selected])]:rounded-tr-[50%] last:[&:has([aria-selected])]:rounded-br-[50%]',
              'calendar-cell',
              `${date?.to && '[&:has([aria-selected])]:bg-custom-cyan-25'}`
            ),
            day_button:
              'size-10 rounded-full text-body-14-regular text-custom-warm-grey-700 cursor-pointer p-0 aria-selected:opacity-100 transition-colors hover:bg-custom-cyan-25 hover:text-custom-cyan-600',
            // v9 `day_*` selection/flag classes → v10 unprefixed
            range_start:
              'day-range-start cursor-pointer aria-selected:bg-custom-cyan-500 aria-selected:text-custom-warm-grey-25 transition-colors aria-selected:hover:bg-custom-cyan-600',
            range_end:
              'day-range-end cursor-pointer aria-selected:bg-custom-cyan-500 aria-selected:text-custom-warm-grey-25 transition-colors aria-selected:hover:bg-custom-cyan-600',
            selected: 'cursor-pointer text-body-14-medium text-custom-warm-grey-25 rounded-full',
            today: 'cursor-pointer bg-custom-warm-grey-100 rounded-full',
            outside:
              'day-outside rounded-full cursor-pointer text-body-14-regular text-custom-warm-grey-400 transition-colors hover:bg-custom-cyan-25 hover:text-custom-cyan-600',
            disabled: 'text-muted-foreground opacity-50',
            range_middle: ' aria-selected:bg-custom-cyan-25 aria-selected:text-custom-cyan-600',
            hidden: 'invisible',
            ...classNames,
          }}
          components={{
            Chevron: ({
              orientation,
              className,
              ...props
            }: {
              orientation?: 'left' | 'right' | 'up' | 'down';
              className?: string;
              [key: string]: unknown;
            }) => {
              const Icon = orientation === 'left' ? ChevronLeftIcon : ChevronRightIcon;
              return (
                <Icon className={cn('size-5 !text-custom-warm-grey-500', className)} {...props} />
              );
            },
          }}
          numberOfMonths={2}
          onSelect={handleSelect}
          mode={mode}
          selected={date}
          {...props}
        />

        <div className="flex items-center justify-between px-6 py-4">
          <div className="flex items-center gap-3">
            <Input
              readOnly
              value={date?.from ? format(date.from, 'MMM d, yyyy') : ''}
              onChange={handleStartDateChange}
              className="max-w-[120px] !bg-transparent"
            />
            <span>-</span>
            <Input
              readOnly
              value={date?.to ? format(date.to, 'MMM d, yyyy') : ''}
              onChange={handleEndDateChange}
              className="max-w-[120px] !bg-transparent"
            />
          </div>
          <div className="flex gap-2 mt-2">
            <Button onClick={handleCancelClick} className="w-[100px]" size="lg" variant="secondary">
              {t('calendar.cancel')}
            </Button>
            <Button onClick={handleSaveClick} className="w-[100px]" size="lg" variant="primary">
              {t('calendar.apply')}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export { Calendar };
